<script lang="ts">
  import { getCurrentRoute, navigate } from '$lib/router.svelte';
  import { getUnreadCount } from '$lib/stores/notifications.svelte';

  const route = $derived(getCurrentRoute());
  const unread = $derived(getUnreadCount());

  const tabs = [
    { path: '/duels', match: ['/duels', '/'], icon: 'play', label: 'Duels' },
    { path: '/catalog', match: ['/catalog'], icon: 'catalog', label: 'Catalog' },
    { path: '/friends', match: ['/friends'], icon: 'friends', label: 'Friends' },
    { path: '/account/notifications/list', match: ['/account/notifications'], icon: 'bell', label: 'Alerts' },
    { path: '/account', match: ['/account'], icon: 'account', label: 'Account' },
  ];

  function isTabActive(tab: (typeof tabs)[0]): boolean {
    return tab.match.some((m) => route.path === m || route.path.startsWith(m + '/'));
  }

  function handleTab(path: string) {
    navigate(path);
  }
</script>

<nav class="tab-bar" aria-label="Navigation">
  {#each tabs as tab}
    <button
      class="tab"
      class:tab--active={isTabActive(tab)}
      onclick={() => handleTab(tab.path)}
      aria-label={tab.label}
      aria-current={isTabActive(tab) ? 'page' : undefined}
    >
      <span class="tab-icon">
        {#if tab.icon === 'play'}
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
            ><polygon points="5,3 19,12 5,21" fill={isTabActive(tab) ? 'currentColor' : 'none'} /></svg
          >
        {:else if tab.icon === 'catalog'}
          <!-- Music library: playlist lines + a note -->
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
            ><path stroke-linecap="round" stroke-linejoin="round" d="M3 6h10M3 10h10M3 14h6" /><circle
              cx="16.5"
              cy="17"
              r="2.5"
            /><path stroke-linecap="round" stroke-linejoin="round" d="M19 17V6l3-1" /></svg
          >
        {:else if tab.icon === 'friends'}
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
            ><path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M17 20h5v-1a4 4 0 00-4-4h-1m-6 5H2v-1a4 4 0 014-4h6a4 4 0 014 4v1zM9 11a3 3 0 100-6 3 3 0 000 6zm8-2a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"
            /></svg
          >
        {:else if tab.icon === 'bell'}
          <span class="tab-bell">
            <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
              ><path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              /></svg
            >
            {#if unread > 0}
              <span class="tab-badge">{unread > 9 ? '9+' : unread}</span>
            {/if}
          </span>
        {:else if tab.icon === 'account'}
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
            ><path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            /></svg
          >
        {/if}
      </span>
      <span class="tab-label">{tab.label}</span>
    </button>
  {/each}
</nav>

<style>
  .tab-bar {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background: var(--surface);
    border-top: 1px solid var(--border);
    padding: 0.25rem 0 env(safe-area-inset-bottom, 0.25rem);
    justify-content: space-around;
  }

  .tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.125rem;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-muted);
    padding: 0.5rem 0.75rem;
    min-width: 3rem;
    min-height: 44px;
    transition: color 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .tab:active {
    transform: scale(0.92);
  }
  .tab--active {
    color: var(--cyan);
  }

  .tab-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
  }
  .tab-label {
    font-size: 0.625rem;
    font-weight: 500;
    letter-spacing: 0.2px;
  }

  .tab-bell {
    position: relative;
    display: flex;
  }
  .tab-badge {
    position: absolute;
    top: -4px;
    right: -8px;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    font-size: 9px;
    font-weight: 700;
    color: #fff;
    background: var(--error);
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 767px) {
    .tab-bar {
      display: flex;
    }
  }
</style>
